<template>
  <!-- DEV-2501. Телефон: основные разделы и «Меню» — в зоне большого пальца, одним
       касанием. Раньше единственной навигацией был бургер в левом верхнем углу. -->
  <nav class="mobile-tab-bar" :aria-label="t('common.navigation.mainSections')">
    <RouterLink
      v-for="item in items"
      :key="item.path"
      :to="item.path ?? '/dashboard'"
      class="mobile-tab-bar__item"
      exact-active-class="mobile-tab-bar__item--active"
    >
      <component :is="item.icon" :size="22" aria-hidden="true" />
      <span class="mobile-tab-bar__label">{{ item.label }}</span>
    </RouterLink>
    <button
      type="button"
      class="mobile-tab-bar__item"
      :aria-expanded="menuOpen"
      @click="emit('menu')"
    >
      <MenuIcon :size="22" aria-hidden="true" />
      <span class="mobile-tab-bar__label">{{ t('common.navigation.menu') }}</span>
    </button>
  </nav>
  <div class="mobile-tab-bar__spacer" aria-hidden="true" />
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { MenuIcon } from 'lucide-vue-next'

import { buildTabBar } from './sidebarNav'
import { useSectionsStore } from '@/features/auth/stores/sections.store'

defineProps<{ menuOpen: boolean }>()
const emit = defineEmits<{ menu: [] }>()

const { t } = useI18n()
const sectionsStore = useSectionsStore()

// Панель живёт только в корне раздела, поэтому текущий раздел — точное совпадение
// адреса: aria-current RouterLink ставит сам.
const items = computed(() => buildTabBar(sectionsStore.navigationKeys, t))
</script>

<style scoped>
/* Как нижняя панель карточки (MobileActionBar): фиксированная полоса, слой — под
   шапкой CRM, чтобы выпадающий список поиска её перекрывал; сайдбар, его затемнение
   и модалки — выше. */
.mobile-tab-bar {
  position: fixed;
  right: 0;
  /* Над полосой звонка (DEV-2502), если она есть. */
  bottom: var(--call-strip-height, 0px);
  left: 0;
  z-index: calc(var(--z-index-header, 30) - 1);
  display: grid;
  grid-auto-columns: minmax(0, 1fr);
  grid-auto-flow: column;
  padding-bottom: env(safe-area-inset-bottom, 0px);
  /* Линия — тенью внутрь: рамка добавила бы панели пиксель сверх 56px отступа. */
  background: var(--color-background-elevated);
  box-shadow: inset 0 1px 0 var(--color-border-default);
}

/* padding и фон — явно: глобальные стили ссылок CRM дают им отступ 3px и заливку
   при наведении. */
.mobile-tab-bar__item {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: calc(var(--spacing-xs) / 2);
  min-height: 56px;
  padding: var(--spacing-xs) calc(var(--spacing-xs) / 2);
  border: none;
  background-color: transparent;
  color: var(--color-text-secondary);
  font-family: inherit;
  font-size: var(--font-size-xs);
  line-height: 1.2;
  text-decoration: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.mobile-tab-bar__item:hover {
  background-color: transparent;
  color: var(--color-text-primary);
}

/* Не только цветом (WCAG 1.4.1): подпись полужирная. */
.mobile-tab-bar__item--active,
.mobile-tab-bar__item--active:hover {
  color: var(--color-primary);
  font-weight: var(--font-weight-semibold);
}

.mobile-tab-bar__item:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: -2px;
}

.mobile-tab-bar__label {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Высота панели с вырезом под домашнюю полосу — на html: её читают и доски, и
   перенесённые в body виджет звонка и тосты. Элемент в фокусе с клавиатуры (и
   прокрутка к нему) — не под панелью. */
:global(html:has(.mobile-tab-bar)) {
  --app-bottom-bar-height: calc(56px + env(safe-area-inset-bottom, 0px));
  scroll-padding-bottom: var(--app-bottom-bar-height);
}

/* Конец страницы не уходит под панель. */
.mobile-tab-bar__spacer {
  height: var(--app-bottom-bar-height);
}

/* Тост — над панелью. Ниже 641px тосты сверху (DashboardLayout, DEV-2502). */
@media (min-width: 641px) {
  :global(html:has(.mobile-tab-bar) .toast-container) {
    bottom: calc(var(--app-bottom-bar-height) + var(--spacing-sm));
  }
}
</style>
